import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, mergeMap, switchMap} from 'rxjs/operators';
import {from, of} from 'rxjs';
import {LoadError} from '@app/core/store/actions/error.actions';
import {
	ApproveAvailability, ApproveAvailabilitySuccess,
	DeleteAvailability,
	DeleteAvailabilitySuccess,
	EAvailabilitiesActions,
	GetApprovedAvailabilities,
	GetApprovedAvailabilitiesSuccess, GetAvailabilities, GetAvailabilitiesSuccess,
	GetUserAvailabilities,
	GetUserAvailabilitiesSuccess,
	PostAvailability,
	PostAvailabilitySuccess,
} from '@app/core/store/actions/availabilities.actions';
import {AvailabilityService} from '@app/modules/shared/services/availability.service';
import {Availability} from '@app/core/models/availability.model';
import {Router} from '@angular/router';
import {PaginationHolder} from '@app/core/models/pagination.model';
import {
	EActivityActions,
	GetRelationActivity,
	GetRelationActivitySuccess
} from '@app/core/store/actions/activity.actions';

@Injectable()
export class AvailabilitiesEffects {

	@Effect()
	public onGetUserAvailabilities$ = this.actions$.pipe(
		ofType<GetUserAvailabilities>(EAvailabilitiesActions.GetUserAvailabilities),
		switchMap((action: GetUserAvailabilities) => from(this.availabilityService.getUserAvailabilities(action.payload, action.page_size, action.page)).pipe(
			switchMap((ph: PaginationHolder<Availability>) => [
				new GetUserAvailabilitiesSuccess(ph.data),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetAvailabilities$ = this.actions$.pipe(
		ofType<GetAvailabilities>(EAvailabilitiesActions.GetAvailabilities),
		switchMap((action: GetAvailabilities) => from(this.availabilityService.getAvailabilities(action.payload, action.page_size, action.page)).pipe(
			switchMap((ph: PaginationHolder<Availability>) => [
				new GetAvailabilitiesSuccess(ph.data),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onGetApprovedAvailabilities$ = this.actions$.pipe(
		ofType<GetApprovedAvailabilities>(EAvailabilitiesActions.GetApprovedAvailabilities),
		switchMap((action: GetApprovedAvailabilities) => from(this.availabilityService.getUserAvailabilities(action.payload, action.page_size, action.page)).pipe(
			switchMap((ph: PaginationHolder<Availability>) => [
				new GetApprovedAvailabilitiesSuccess(ph.data, ph.pagination),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPostAvailability$ = this.actions$.pipe(
		ofType<PostAvailability>(EAvailabilitiesActions.PostAvailability),
		switchMap((action: PostAvailability) => from(this.availabilityService.post(action.payload)).pipe(
			switchMap((availability: Availability) => [
				new PostAvailabilitySuccess(availability),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onPostAvailabilitySuccess$ = this.actions$.pipe(
		ofType<PostAvailabilitySuccess>(EAvailabilitiesActions.PostAvailabilitySuccess),
		switchMap((action: PostAvailabilitySuccess) => {
			this.router.navigate(['./home/tilgængelighed']);

			return [];
		}),
	);

	@Effect()
	public onDeleteAvailability$ = this.actions$.pipe(
		ofType<DeleteAvailability>(EAvailabilitiesActions.DeleteAvailability),
		switchMap((action: DeleteAvailability) => from(this.availabilityService.delete(action.id)).pipe(
			switchMap((availability: Availability) => [
				new DeleteAvailabilitySuccess(availability),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	@Effect()
	public onApproveAvailability$ = this.actions$.pipe(
		ofType<ApproveAvailability>(EAvailabilitiesActions.ApproveAvailability),
		mergeMap((action: ApproveAvailability) => from(this.availabilityService.approve(action.availability.id)).pipe(
			mergeMap((availability: Availability) => [
				new ApproveAvailabilitySuccess(availability),
			]),
			catchError(error => of(new LoadError(error, action))),
		)),
	);

	constructor(
		private actions$: Actions,
		private availabilityService: AvailabilityService,
		private router: Router,
	) {
	}
}
